import { SpecialDIIds } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React from "react";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { useQuickOpenState } from "./quick-props/quick-controls";
import { toDisplayLabel } from "./quick-props/quick-labels";
import { ICollapsedPath, ISelectionCrumb } from "./selection-path";

export interface ISelectionBreadcrumbProps {
  /** `compactSelectionPath`: every ancestor behind the "…", then the selected component. */
  path: ICollapsedPath;
  /** Selects an ancestor. */
  onSelect: (kuikaId: string) => void;
  /** Outlines a crumb's component on the canvas while it is hovered; null clears the outline. */
  onHover: (kuikaId: string | null) => void;
  /** Where the ancestors menu may open (the canvas region). */
  collisionBoundary?: Element | null;
  elementRef?: React.Ref<HTMLElement>;
}

const ROOT_KUIKA_ID: string = SpecialDIIds.rootDI.toString();

/**
 * Test id of one crumb: the friendly name, which is unique per screen. The screen root has none — it shows the
 * screen's (renamable, free-text) name — so it gets a literal "root", as the footer breadcrumb does.
 */
const crumbTestId = (crumb: ISelectionCrumb): string =>
  `selectionCrumb-${crumb.kuikaId === ROOT_KUIKA_ID ? "root" : crumb.name}`;

/**
 * The breadcrumb, always merged into the start of the selection bar (after the drag grip): `… › Name`.
 * The selected component shows its name alone — every pixel of the bar is taken from the quick props —
 * and its type and full path are in its tooltip. The "…" opens the whole ancestor path, screen first;
 * a row selects that ancestor and outlines it on the canvas while hovered. The screen root has no "…".
 */
export const SelectionBreadcrumb: React.FC<ISelectionBreadcrumbProps> = ({
  path,
  onSelect,
  onHover,
  collisionBoundary,
  elementRef
}: ISelectionBreadcrumbProps): JSX.Element | null => {
  const [isMenuOpen, setIsMenuOpen] = useQuickOpenState();
  const crumbs: ISelectionCrumb[] = [...path.head, ...path.hidden, ...path.tail];
  const current: ISelectionCrumb | undefined = crumbs[crumbs.length - 1];
  if (current === undefined) {
    return null;
  }
  const ancestors: ISelectionCrumb[] = crumbs.slice(0, -1);
  const fullPathLabel: string = crumbs.map((crumb: ISelectionCrumb): string => crumb.name).join(" › ");
  const hasType: boolean = current.type !== "" && current.type !== current.name;
  const typeLabel: string = toDisplayLabel(current.type);

  const menu: JSX.Element = (
    <div className="kuika-selection-menu kuika-selection-menu--scroll" role="menu" aria-label="Parents">
      {ancestors.map((crumb: ISelectionCrumb): JSX.Element => (
        <button
          key={crumb.kuikaId}
          type="button"
          role="menuitem"
          className="kuika-selection-menu__item"
          data-testid={crumbTestId(crumb)}
          onClick={(): void => {
            setIsMenuOpen(false);
            onHover(null);
            onSelect(crumb.kuikaId);
          }}
          onMouseEnter={(): void => onHover(crumb.kuikaId)}
          onMouseLeave={(): void => onHover(null)}
          onFocus={(): void => onHover(crumb.kuikaId)}
          onBlur={(): void => onHover(null)}
        >
          <span className="kuika-selection-menu__label">{crumb.name}</span>
          <span className="kuika-selection-menu__hint">{toDisplayLabel(crumb.type)}</span>
        </button>
      ))}
    </div>
  );

  return (
    <nav
      ref={elementRef as React.Ref<HTMLElement>}
      className="kuika-selection-crumbs"
      aria-label="Selection path"
      data-testid="selectionBreadcrumb"
    >
      {ancestors.length > 0 && (
        <>
          <KuikaPopover
            tone="inverse"
            visible={isMenuOpen}
            onVisibleChange={setIsMenuOpen}
            position="bottom"
            align="start"
            sideOffset={6}
            collisionPadding={8}
            collisionBoundary={collisionBoundary ?? undefined}
            content={menu}
          >
            <button
              type="button"
              className={classNames("kuika-selection-crumbs__crumb", "kuika-selection-crumbs__ellipsis", {
                "kuika-selection-crumbs__crumb--open": isMenuOpen
              })}
              aria-label="Show parents"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              data-testid="selectionCrumbEllipsis"
            >
              …
            </button>
          </KuikaPopover>
          <KuikaSvg
            name="chevron-right"
            type="arrows"
            width={12}
            height={12}
            color="var(--selection-bar-muted)"
            className="kuika-selection-crumbs__separator"
            aria-hidden="true"
          />
        </>
      )}
      <KuikaTooltip
        content={hasType ? `${fullPathLabel} · ${typeLabel}` : fullPathLabel}
        position="top"
        bgcolor="inverse"
      >
        <span
          className="kuika-selection-crumbs__crumb kuika-selection-crumbs__crumb--current"
          aria-current="true"
          aria-label={hasType ? `${current.name}, ${typeLabel}` : current.name}
          data-testid={crumbTestId(current)}
        >
          <span className="kuika-selection-crumbs__name">{current.name}</span>
        </span>
      </KuikaTooltip>
    </nav>
  );
};
